// src/admin/pages/users/userProfile.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Divider,
  IconButton,
  InputAdornment,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Tooltip,
  Paper,
  Card,
  CardContent,
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelCircleIcon,
  Login as LoginIcon,
  CalendarToday as CalendarTodayIcon,
  Update as UpdateIcon,
  VerifiedUser as VerifiedUserIcon,
  ManageAccounts as ManageAccountsIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import {
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../../firebase/config';
import { useAuth } from '../../../contexts/AuthContext';
import { UserService } from './userService';
import { useUserClaims, useInvalidateUserClaims } from '../../../hooks/useUserProfile';
import ToastNotification from '../common/Toastnotification';

interface ProfileFormData {
  displayName: string;
  email: string;
  photoURL: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const formatProvider = (providerId: string): string => {
  switch (providerId) {
    case 'password':     return 'Email & Password';
    case 'google.com':   return 'Google';
    case 'facebook.com': return 'Facebook';
    case 'github.com':   return 'GitHub';
    case 'twitter.com':  return 'Twitter / X';
    case 'apple.com':    return 'Apple';
    case 'phone':        return 'Phone Number';
    default:             return providerId;
  }
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return 'Unknown';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

const SectionHeader: React.FC<{
  icon: React.ReactElement;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}> = ({ icon, title, subtitle, action }) => (
  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 3 }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          width: 36, height: 36, borderRadius: 1.5,
          bgcolor: 'primary.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { color: 'primary.contrastText', fontSize: 18 } } as object)}
      </Box>
      <Box>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary" lineHeight={1.2}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
    </Stack>
    {action}
  </Stack>
);

const InfoRow: React.FC<{ icon: React.ReactElement; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'flex-start', gap: 2,
      p: 2, transition: 'background 0.15s',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <Box
      sx={{
        width: 32, height: 32, borderRadius: 1,
        bgcolor: 'action.selected',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, mt: 0.2,
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 16, color: 'text.secondary' } } as object)}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.3 }}>
        {label}
      </Typography>
      <Box>{value}</Box>
    </Box>
  </Box>
);

const UserProfile: React.FC = () => {
  const { currentUser, refreshUserProfile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── React Query: load user claims (role) ─────────────────────────────────
  const { data: claimsData, isLoading: claimsLoading } = useUserClaims(currentUser?.uid);
  const invalidateClaims = useInvalidateUserClaims();

  // ── Derived role from cache ───────────────────────────────────────────────
  const userRole = claimsData?.role || 'user';

  // ── UI state (unchanged from original) ───────────────────────────────────
  const [saving, setSaving]                 = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage]   = useState<File | null>(null);
  const [previewURL, setPreviewURL]         = useState<string>('');
  const [editingProfile, setEditingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [profileData, setProfileData] = useState<ProfileFormData>({
    displayName: '', email: '', photoURL: '',
  });
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '', newPassword: '', confirmPassword: '',
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword]         = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  const showToast = (message: string, severity: ToastState['severity']) =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((p) => ({ ...p, open: false }));

  // Sync profileData from currentUser whenever auth state changes
  // (replaces the original loadUserData() which also called getUserClaims —
  //  claims are now handled by React Query above)
  useEffect(() => {
    if (!currentUser) return;
    setProfileData({
      displayName: currentUser.displayName || '',
      email: currentUser.email || '',
      photoURL: currentUser.photoURL || '',
    });
  }, [currentUser]);

  // Revoke object URL on unmount to avoid memory leaks
  useEffect(() => {
    return () => { if (previewURL) URL.revokeObjectURL(previewURL); };
  }, [previewURL]);

  // ── Handlers (unchanged from original) ───────────────────────────────────

  const handleProfileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData((p) => ({ ...p, [name]: value }));
  };

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((p) => ({ ...p, [name]: value }));
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please select a valid image file', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('Image size must be less than 5MB', 'error'); return; }
    setSelectedImage(file);
    if (previewURL) URL.revokeObjectURL(previewURL);
    setPreviewURL(URL.createObjectURL(file));
    showToast('Image selected', 'success');
  };

  const handleUploadImage = async (): Promise<string | null> => {
    if (!selectedImage || !currentUser) return null;
    try {
      setUploadingImage(true);
      const ext = selectedImage.name.split('.').pop();
      const storageRef = ref(storage, `profile_pictures/profile_${currentUser.uid}_${Date.now()}.${ext}`);
      await uploadBytes(storageRef, selectedImage);
      const url = await getDownloadURL(storageRef);
      if (currentUser.photoURL?.includes('firebase')) {
        try { await deleteObject(ref(storage, currentUser.photoURL)); } catch {}
      }
      return url;
    } catch (err: any) {
      throw new Error('Failed to upload image: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    try {
      setSaving(true);
      let photoURL = profileData.photoURL;
      if (selectedImage) {
        showToast('Uploading photo…', 'info');
        const uploaded = await handleUploadImage();
        if (uploaded) photoURL = uploaded;
      }
      await updateProfile(currentUser, { displayName: profileData.displayName, photoURL: photoURL || null });
      if (profileData.email !== currentUser.email) await updateEmail(currentUser, profileData.email);
      await UserService.createUserClaims(currentUser.uid, profileData.email, profileData.displayName, photoURL);
      showToast('Profile updated successfully!', 'success');
      setEditingProfile(false);
      setSelectedImage(null);
      if (previewURL) { URL.revokeObjectURL(previewURL); setPreviewURL(''); }
      await refreshUserProfile();
      // Bust the React Query cache so the updated claims are re-fetched
      invalidateClaims(currentUser.uid);
    } catch (err: any) {
      const code = err.code;
      if (code === 'auth/requires-recent-login') showToast('Please re-login to update your email.', 'error');
      else if (code === 'auth/email-already-in-use') showToast('Email already in use.', 'error');
      else if (code === 'auth/invalid-email') showToast('Invalid email address.', 'error');
      else showToast(err.message || 'Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.email) return;
    if (passwordData.newPassword.length < 6) { showToast('Password must be at least 6 characters', 'error'); return; }
    if (passwordData.newPassword !== passwordData.confirmPassword) { showToast('Passwords do not match', 'error'); return; }
    try {
      setSaving(true);
      const cred = EmailAuthProvider.credential(currentUser.email, passwordData.currentPassword);
      await reauthenticateWithCredential(currentUser, cred);
      await updatePassword(currentUser, passwordData.newPassword);
      showToast('Password updated!', 'success');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setChangingPassword(false);
    } catch (err: any) {
      const code = err.code;
      if (code === 'auth/wrong-password') showToast('Current password is incorrect', 'error');
      else if (code === 'auth/weak-password') showToast('Password is too weak', 'error');
      else if (code === 'auth/too-many-requests') showToast('Too many attempts. Try again later.', 'warning');
      else showToast(err.message || 'Failed to update password', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (currentUser) setProfileData({ displayName: currentUser.displayName || '', email: currentUser.email || '', photoURL: currentUser.photoURL || '' });
    setEditingProfile(false);
    setSelectedImage(null);
    if (previewURL) { URL.revokeObjectURL(previewURL); setPreviewURL(''); }
  };

  const handleCancelPasswordChange = () => {
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setChangingPassword(false);
  };

  // ── Guards ────────────────────────────────────────────────────────────────

  if (claimsLoading && !currentUser) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading profile...</Typography>
      </Box>
    );
  }

  if (!currentUser) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="warning">Please log in to view your profile.</Alert>
      </Box>
    );
  }

  const displayPhotoURL = previewURL || profileData.photoURL;
  const providers = currentUser.providerData.map((p) => formatProvider(p.providerId));
  const isEmailProvider = currentUser.providerData.some((p) => p.providerId === 'password');

  const getRoleColor = (): 'error' | 'warning' | 'info' | 'default' => {
    if (userRole === 'admin')   return 'error';
    if (userRole === 'manager') return 'warning';
    if (userRole === 'staff')   return 'info';
    return 'default';
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        bgcolor: 'background.default',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'divider', borderRadius: '3px' },
      }}
    >
      {/* ── Hero Banner ── */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          pt: { xs: 4, sm: 5 },
          pb: { xs: 8, sm: 10 },
          px: { xs: 2.5, sm: 4 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""', position: 'absolute',
            width: 280, height: 280, borderRadius: '50%',
            bgcolor: 'primary.light', opacity: 0.15,
            top: -80, right: -60,
          },
          '&::after': {
            content: '""', position: 'absolute',
            width: 180, height: 180, borderRadius: '50%',
            bgcolor: 'primary.dark', opacity: 0.15,
            bottom: -50, left: 30,
          },
        }}
      >
        <Box sx={{ maxWidth: 760, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <PersonIcon sx={{ fontSize: { xs: 26, sm: 32 }, color: 'primary.contrastText' }} />
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} color="primary.contrastText">
                My Profile
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.8 }}>
                Manage your account settings and preferences
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {[
              { label: 'Role',      value: userRole.toUpperCase() },
              { label: 'Status',    value: 'Active' },
              { label: 'Providers', value: currentUser.providerData.length },
            ].map((s) => (
              <Grid item xs={4} key={s.label}>
                <Box
                  sx={{
                    bgcolor: 'primary.dark', opacity: 0.85,
                    borderRadius: 1, p: { xs: 1, sm: 1.5 }, textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" color="primary.contrastText" fontWeight={700}
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {s.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.75 }}>
                    {s.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── Cards ── */}
      <Box sx={{ maxWidth: 760, mx: 'auto', px: { xs: 2, sm: 3 }, mt: -5, pb: 5 }}>

        {/* ── Profile Card ── */}
        <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: { xs: 2.5, sm: 3 }, pb: 2.5 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'center', sm: 'flex-end' }}
              spacing={2}
            >
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                  src={displayPhotoURL}
                  sx={{
                    width: { xs: 88, sm: 100 }, height: { xs: 88, sm: 100 },
                    border: '4px solid',
                    borderColor: 'background.paper',
                    boxShadow: 3,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                  }}
                >
                  {profileData.displayName?.[0]?.toUpperCase() || profileData.email?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                {editingProfile && (
                  <Tooltip title="Upload new photo (max 5MB)">
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={saving || uploadingImage}
                      sx={{
                        position: 'absolute', bottom: 2, right: 2,
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        width: 28, height: 28,
                        '&:hover': { bgcolor: 'primary.dark' },
                        boxShadow: 2,
                      }}
                    >
                      {uploadingImage
                        ? <CircularProgress size={14} color="inherit" />
                        : <PhotoCameraIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={700}>
                  {profileData.displayName || 'No Name Set'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', mb: 1 }}>
                  {profileData.email}
                </Typography>
                <Chip label={userRole.toUpperCase()} color={getRoleColor()} size="small" />
              </Box>

              {!editingProfile && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setEditingProfile(true)}
                  variant="contained"
                  size="small"
                  fullWidth={isMobile}
                  color="primary"
                >
                  Edit Profile
                </Button>
              )}
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 2.5, pb: 3 }}>
            <form onSubmit={handleUpdateProfile}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Display Name" name="displayName"
                    value={profileData.displayName}
                    onChange={handleProfileInputChange}
                    disabled={!editingProfile || saving}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Email Address" name="email"
                    value={profileData.email}
                    disabled
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ fontSize: 16 }} color="disabled" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {editingProfile && (
                  <Grid item xs={12}>
                    {selectedImage && (
                      <Fade in>
                        <Typography variant="caption" color="success.main" display="block" sx={{ mb: 1.5 }}>
                          ✓ {selectedImage.name} ready to upload
                        </Typography>
                      </Fade>
                    )}
                    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                      <Button
                        onClick={handleCancelEdit}
                        disabled={saving || uploadingImage}
                        startIcon={<CancelIcon />}
                        size="small"
                        fullWidth={isMobile}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={saving || uploadingImage}
                        size="small"
                        fullWidth={isMobile}
                        startIcon={saving || uploadingImage ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                      >
                        {uploadingImage ? 'Uploading…' : saving ? 'Saving…' : 'Save Changes'}
                      </Button>
                    </Stack>
                  </Grid>
                )}
              </Grid>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} style={{ display: 'none' }} />
            </form>
          </Box>
        </Paper>

        {/* ── Password Card ── */}
        <Paper elevation={2} sx={{ mb: 3, p: { xs: 2.5, sm: 3.5 } }}>
          <SectionHeader
            icon={<ShieldIcon />}
            title="Password & Security"
            subtitle="Keep your account protected"
            action={
              !changingPassword && isEmailProvider ? (
                <Button
                  startIcon={<LockIcon />}
                  onClick={() => setChangingPassword(true)}
                  variant="outlined"
                  size="small"
                  color="primary"
                  sx={{ flexShrink: 0 }}
                >
                  {isMobile ? 'Change' : 'Change Password'}
                </Button>
              ) : undefined
            }
          />

          {!isEmailProvider && !changingPassword ? (
            <Alert severity="info">
              Password management is not available for <strong>{providers.join(', ')}</strong> sign-in.
              Manage your password through your identity provider.
            </Alert>
          ) : changingPassword ? (
            <form onSubmit={handleUpdatePassword}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Current Password" name="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={handlePasswordInputChange}
                    disabled={saving} required size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                            {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="New Password" name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={handlePasswordInputChange}
                    disabled={saving} required size="small"
                    helperText="At least 6 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                            {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Confirm New Password" name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordInputChange}
                    disabled={saving} required size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                            {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                    <Button
                      onClick={handleCancelPasswordChange} disabled={saving}
                      startIcon={<CancelIcon />} size="small" fullWidth={isMobile}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit" variant="contained" disabled={saving}
                      fullWidth={isMobile} size="small"
                      startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    >
                      {saving ? 'Updating…' : 'Update Password'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          ) : (
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                p: 2, bgcolor: 'action.hover', borderRadius: 1,
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                Use a unique, strong password for maximum security.
              </Typography>
            </Box>
          )}
        </Paper>

        {/* ── Account Information Card ── */}
        <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: { xs: 2.5, sm: 3 }, pb: 0 }}>
            <SectionHeader
              icon={<ManageAccountsIcon />}
              title="Account Information"
              subtitle="Your account details and activity"
            />
          </Box>

          <Divider />

          <Grid container sx={{ pb: 1 }}>
            {(([
              {
                icon: <PersonIcon />, label: 'Display Name',
                value: currentUser.displayName
                  ? <Typography variant="body2" fontWeight={500}>{currentUser.displayName}</Typography>
                  : <Typography variant="body2" color="text.disabled" fontStyle="italic">Not set</Typography>,
              },
              // {
              //   icon: <VerifiedUserIcon />, label: 'Email Verified',
              //   value: (
              //     <Chip
              //       icon={currentUser.emailVerified
              //         ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
              //         : <CancelCircleIcon sx={{ fontSize: '14px !important' }} />}
              //       label={currentUser.emailVerified ? 'Verified' : 'Not Verified'}
              //       color={currentUser.emailVerified ? 'success' : 'warning'}
              //       size="small" variant="outlined"
              //       sx={{ fontWeight: 600, fontSize: '0.72rem' }}
              //     />
              //   ),
              // },
              {
                icon: <LoginIcon />, label: 'Sign-in Method',
                value: (
                  <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                    {providers.map((p) => (
                      <Chip key={p} label={p} size="small" color="primary" variant="outlined"
                        sx={{ fontSize: '0.72rem', fontWeight: 600 }}
                      />
                    ))}
                  </Stack>
                ),
              },
              {
                icon: <LockIcon />, label: 'Role',
                value: (
                  <Chip
                    label={userRole.toUpperCase()}
                    color={getRoleColor()}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: '0.65rem' }}
                  />
                ),
              },
              {
                icon: <CalendarTodayIcon />, label: 'Account Created',
                value: <Typography variant="body2" fontWeight={500}>{formatDate(currentUser.metadata.creationTime)}</Typography>,
              },
              {
                icon: <UpdateIcon />, label: 'Last Sign In',
                value: <Typography variant="body2" fontWeight={500}>{formatDate(currentUser.metadata.lastSignInTime)}</Typography>,
              },
              ...(currentUser.phoneNumber ? [{
                icon: <EmailIcon />, label: 'Phone Number',
                value: <Typography variant="body2" fontWeight={500}>{currentUser.phoneNumber}</Typography>,
              }] : []),
              // {
              //   icon: <ManageAccountsIcon />, label: 'Linked Accounts',
              //   value: (
              //     <Typography variant="body2" fontWeight={500}>
              //       {currentUser.providerData.length} provider{currentUser.providerData.length !== 1 ? 's' : ''} connected
              //     </Typography>
              //   ),
              // },
            ] as { icon: React.ReactElement; label: string; value: React.ReactNode }[])).map((row, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <InfoRow icon={row.icon} label={row.label} value={row.value} />
              </Grid>
            ))}
          </Grid>
        </Paper>

      </Box>

      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />
    </Box>
  );
};

export default UserProfile;